import React, { useState, useEffect, useRef, useContext } from 'react';
import { useParams, Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { authFetch, fetchCsrfToken } from './authFetch';
import { UserContext } from './App';
import DocumentList from './DocumentList';
import FormsList from './FormsList';
import PolicyTab from './PolicyTab';
import ScanButton from './ScanButton';


const BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Fields locked for non-admin users after a Florida Blue import match
const IMPORT_LOCKED_FIELDS = new Set([
  'first_name', 'last_name', 'email', 'phone', 'dob',
  'street_address', 'address_line2', 'city', 'state', 'zip_code',
  'county', 'fl_blue_member_id',
]);

function CustomerProfile() {
  const { currentUser } = useContext(UserContext);
  // Check for both Admin and Agent roles
  const hasAdminRole = currentUser?.groups?.some(g => g.name === 'Admin');
  const hasAgentRole = currentUser?.groups?.some(g => g.name === 'Agent');
  const canEditCustomer = hasAdminRole || hasAgentRole;
  
  // Field-level permissions
  const canEditAllFields = hasAdminRole; // Only admins can edit all fields
  const canEditStatusFields = hasAgentRole || hasAdminRole; // Both can edit status/app_id
  const timeZone = 'America/New_York'; // User's timezone

  const { id } = useParams();
  const [customer, setCustomer] = useState(null);

  const [editingCustomer, setEditingCustomer] = useState(false);
  const [editedCustomerData, setEditedCustomerData] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    dob: '', street_address: '', address_line2: '', city: '', state: '', zip_code: '',
    referral_source: '', manual_referral: '', county: '', manual_county: '',
    ssn: '', fl_blue_member_id: '', smoker_status: 'Never', status: 'Active', app_id: ''
  });
  const [manualReferralInput, setManualReferralInput] = useState('');
  const [manualCountyInput, setManualCountyInput] = useState('');

  const [notes, setNotes] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [newAppointment, setNewAppointment] = useState({ title: '', date: '', location: '', note: '' });
  const [editingAppointmentId, setEditingAppointmentId] = useState(null);
  const [activeTab, setActiveTab] = useState('notes');

  // Status tracking
  const [showExpiredModal, setShowExpiredModal] = useState(false);
  const [expiredExplanation, setExpiredExplanation] = useState('');
  const [showAppIdPrompt, setShowAppIdPrompt] = useState(false);
  const [appIdInput, setAppIdInput] = useState('');
  const [pendingStatusChange, setPendingStatusChange] = useState(null);

  // Status messages
  const [statusMessage, setStatusMessage] = useState({ text: '', type: '' });

  const NOTES_PER_PAGE = 8;
  const APPOINTMENTS_PER_PAGE = 8;
  const [notesPage, setNotesPage] = useState(1);
  const [appointmentsPage, setAppointmentsPage] = useState(1);

  const sortedNotes = [...notes].reverse();
  const paginatedNotes = sortedNotes.slice((notesPage - 1) * NOTES_PER_PAGE, notesPage * NOTES_PER_PAGE);
  const sortedAppointments = [...appointments].reverse();
  const paginatedAppointments = sortedAppointments.slice((appointmentsPage - 1) * APPOINTMENTS_PER_PAGE, appointmentsPage * APPOINTMENTS_PER_PAGE);
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [showAppointmentForm, setShowAppointmentForm] = useState(false);

  const noteInputRef = useRef(null);
  const apptTitleRef = useRef(null);

  // Define smoker status options
  const smokerStatusOptions = [
    "Never", 
    "Daily", 
    "Former", 
    "Occasional", 
    "Second Hand", 
    "Unknown"
  ];

  // Define status options
  const statusOptions = [
    { value: "Active", label: "Lead: Active" },
    { value: "Expired", label: "Expired" },
    { value: "Sold", label: "Sold" }
  ];

  // Function to show status messages
  const showStatus = (text, type = 'success') => {
    setStatusMessage({ text, type });
    
    // Auto-hide the message after 5 seconds
    setTimeout(() => {
      setStatusMessage({ text: '', type: '' });
    }, 5000);
  };

  // Handle status change with special logic for Expired and Sold
  const handleStatusChange = (newStatus) => {
    if (newStatus === "Expired") {
      setPendingStatusChange(newStatus);
      setShowExpiredModal(true);
    } else if (newStatus === "Sold") {
      setPendingStatusChange(newStatus);
      setAppIdInput(editedCustomerData.app_id || '');
      setShowAppIdPrompt(true);
    } else {
      // For "Active" status, just update directly
      updateField('status', newStatus);
    }
  };

  // Handle expired explanation submission
  const handleExpiredSubmit = async () => {
    if (!expiredExplanation.trim()) {
      showStatus('Please provide an explanation for expired status.', 'error');
      return;
    }

    try {
      // FIXED: Actually save the status change to the database
      const csrfToken = await fetchCsrfToken();
      if (!csrfToken) {
        throw new Error('Could not get CSRF token');
      }

      // 1. Update the customer status in the database
      const statusUpdateResponse = await authFetch(`${BASE_URL}/api/customers/${id}/`, {
        method: 'PATCH', // Use PATCH for partial update
        body: JSON.stringify({ 
          status: pendingStatusChange 
        }),
      });

      if (!statusUpdateResponse.ok) {
        throw new Error('Failed to update customer status');
      }

      // 2. Add the explanation as a note
      const noteResponse = await authFetch(`${BASE_URL}/api/notes/`, {
        method: 'POST',
        body: JSON.stringify({ 
          customer: id, 
          content: `Status changed to Expired: ${expiredExplanation}` 
        }),
      });

      if (!noteResponse.ok) {
        throw new Error('Failed to add note');
      }

      // 3. Reset modal state and refresh data
      setShowExpiredModal(false);
      setExpiredExplanation('');
      setPendingStatusChange(null);
      
      // Refresh customer data and notes
      await fetchData();
      showStatus('Status updated to Expired and explanation added to notes.', 'success');
    } catch (error) {
      console.error('Error handling expired status:', error);
      showStatus('Failed to update status or add explanation note.', 'error');
    }
  };

  // Handle sold status and APP ID submission
  const handleSoldSubmit = async () => {
    if (!appIdInput.trim()) {
      showStatus('Please provide an APP ID for sold status.', 'error');
      return;
    }

    try {
      // FIXED: Actually save the status and app_id changes to the database
      const csrfToken = await fetchCsrfToken();
      if (!csrfToken) {
        throw new Error('Could not get CSRF token');
      }

      // Update both status and app_id in the database
      const updateResponse = await authFetch(`${BASE_URL}/api/customers/${id}/`, {
        method: 'PATCH', // Use PATCH for partial update
        body: JSON.stringify({ 
          status: pendingStatusChange,
          app_id: appIdInput.trim()
        }),
      });

      if (!updateResponse.ok) {
        throw new Error('Failed to update customer');
      }

      // Add a note about the status change
      const noteResponse = await authFetch(`${BASE_URL}/api/notes/`, {
        method: 'POST',
        body: JSON.stringify({ 
          customer: id, 
          content: `Status changed to Sold. APP ID: ${appIdInput.trim()}` 
        }),
      });

      if (!noteResponse.ok) {
        // Don't fail the whole operation if note fails
        console.warn('Failed to add note, but status was updated successfully');
      }

      // Reset modal state
      setShowAppIdPrompt(false);
      setAppIdInput('');
      setPendingStatusChange(null);
      
      // Refresh customer data
      await fetchData();
      showStatus('Status updated to Sold and APP ID set.', 'success');
    } catch (error) {
      console.error('Error handling sold status:', error);
      showStatus('Failed to update status or APP ID.', 'error');
    }
  };

  // Handle modal cancellation
  const handleModalCancel = () => {
    setShowExpiredModal(false);
    setShowAppIdPrompt(false);
    setExpiredExplanation('');
    setAppIdInput('');
    setPendingStatusChange(null);
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  useEffect(() => {
    if (showNoteForm && noteInputRef.current) noteInputRef.current.focus();
  }, [showNoteForm]);

  useEffect(() => {
    if (showAppointmentForm && apptTitleRef.current) apptTitleRef.current.focus();
  }, [showAppointmentForm]);

  const fetchData = async () => {
  try {
      // Enhanced debugging to see user object structure
      console.log("Current user:", currentUser);
      console.log("Current user (full object):", JSON.stringify(currentUser, null, 2));
      console.log("User groups:", currentUser?.groups);
      console.log("Has admin role:", hasAdminRole);
      console.log("Has agent role:", hasAgentRole);
      console.log("Can edit customer:", canEditCustomer);
      console.log("Can edit all fields:", canEditAllFields);
      console.log("Can edit status fields:", canEditStatusFields);
      
      const [customerRes, notesRes, apptRes] = await Promise.all([
          authFetch(`${BASE_URL}/api/customers/${id}/`),
          authFetch(`${BASE_URL}/api/notes/?customer_id=${id}`),
          authFetch(`${BASE_URL}/api/appointments/?customer_id=${id}`)
      ]);

      if (!customerRes.ok || !notesRes.ok || !apptRes.ok) {
          throw new Error("One or more API requests failed");
      }
      
      const customerData = await customerRes.json();
      setCustomer(customerData);
      setEditedCustomerData(customerData);
      setManualReferralInput(customerData.manual_referral || '');
      setManualCountyInput(customerData.manual_county || '');

      const notesData = await notesRes.json();
      console.log("📝 Raw notes response:", notesData);
      
      // FIXED: Handle paginated response format
      const processedNotes = Array.isArray(notesData) ? notesData : (notesData?.results || []);
      console.log("📝 Processed notes:", processedNotes);
      setNotes(processedNotes);
      
      const apptData = await apptRes.json();
      console.log("📅 Raw appointments response:", apptData);
      
      // FIXED: Handle paginated response format  
      const processedAppointments = Array.isArray(apptData) ? apptData : (apptData?.results || []);
      console.log("📅 Processed appointments:", processedAppointments);
      setAppointments(processedAppointments);
      
      console.log("✅ fetchData completed successfully");
  } catch(error) {
      console.error("💥 Error in fetchData:", error);
  }
};

  const handleCustomerEdit = async () => {
    if (!window.confirm("Are you sure you want to save these customer changes?")) {
      return;
    }
    
    try {
      // FIXED: Wait for CSRF token and verify it was received
      const csrfToken = await fetchCsrfToken();
      if (!csrfToken) {
        throw new Error('Could not get CSRF token');
      }
      console.log('Customer edit - CSRF token received:', csrfToken.substring(0, 10) + '...');
      
      const dataToSend = {
        ...editedCustomerData,
        referral_source: editedCustomerData.referral_source === 'Manual' ? manualReferralInput : editedCustomerData.referral_source,
        manual_referral: editedCustomerData.referral_source === 'Manual' ? manualReferralInput : '',
        county: editedCustomerData.county === 'Manual' ? manualCountyInput : editedCustomerData.county,
        manual_county: editedCustomerData.county === 'Manual' ? manualCountyInput : ''
      };
      
      // UPDATED: Added /api/ prefix
      const response = await authFetch(`${BASE_URL}/api/customers/${id}/`, {
        method: 'PUT',
        body: JSON.stringify(dataToSend),
      });
      
      if (!response.ok) {
        console.error('Failed to update customer:', response.status);
        showStatus('Failed to update customer information.', 'error');
        return;
      }
      
      setEditingCustomer(false);
      await fetchData();
      showStatus('Customer information updated successfully!', 'success');
    } catch (error) {
      console.error('Error updating customer:', error);
      showStatus('An error occurred while updating customer information.', 'error');
    }
  };

  const updateField = (field, value) => {
    setEditedCustomerData({ ...editedCustomerData, [field]: value });
  };

  const referralOptions = ['Google', 'Facebook', 'Friend', 'Manual'];
  const countyOptions = ['Alachua', 'Baker', 'Bay', 'Bradford', 'Brevard', 'Broward', 'Calhoun', 'Charlotte', 'Citrus', 'Clay', 'Collier', 'Columbia', 'DeSoto', 'Dixie', 'Duval', 'Escambia', 'Flagler', 'Franklin', 'Gadsden', 'Gilchrist', 'Glades', 'Gulf', 'Hamilton', 'Hardee', 'Hendry', 'Hernando', 'Highlands', 'Hillsborough', 'Holmes', 'Indian River', 'Jackson', 'Jefferson', 'Lafayette', 'Lake', 'Lee', 'Leon', 'Levy', 'Liberty', 'Madison', 'Manatee', 'Marion', 'Martin', 'Miami-Dade', 'Monroe', 'Nassau', 'Okaloosa', 'Okeechobee', 'Orange', 'Osceola', 'Palm Beach', 'Pasco', 'Pinellas', 'Polk', 'Putnam', 'Santa Rosa', 'Sarasota', 'Seminole', 'St. Johns', 'St. Lucie', 'Sumter', 'Suwannee', 'Taylor', 'Union', 'Volusia', 'Wakulla', 'Walton', 'Washington', 'Manual'];

  const handleEditAppointment = (appt) => {
    const localDate = toZonedTime(new Date(appt.date), timeZone);
    const formattedForInput = format(localDate, "yyyy-MM-dd'T'HH:mm");

    console.log(`Editing appointment with ID: ${appt.id}`);
    console.log(`Appointment data:`, appt);
    console.log(`Local formatted date: ${formattedForInput}`);
    
    setShowAppointmentForm(true);
    setNewAppointment({
        title: appt.title,
        date: formattedForInput,
        location: appt.location,
        note: appt.note,
    });
    setEditingAppointmentId(appt.id);
  };

  const handleAddOrUpdateAppointment = async () => {
    // Validate form data
    if (!newAppointment.title || !newAppointment.date) {
      showStatus('Title and date are required fields.', 'error');
      return;
    }
    
    // Confirm action
    const actionText = editingAppointmentId ? "update" : "create";
    if (!window.confirm(`Are you sure you want to ${actionText} this appointment?`)) {
      return;
    }
    
    try {
      // FIXED: Wait for CSRF token and verify it was received
      const csrfToken = await fetchCsrfToken();
      if (!csrfToken) {
        throw new Error('Could not get CSRF token');
      }
      console.log('Appointment save - CSRF token received:', csrfToken.substring(0, 10) + '...');
      
      // UPDATED: Added /api/ prefix and logging
      const url = editingAppointmentId 
          ? `${BASE_URL}/api/appointments/${editingAppointmentId}/`
          : `${BASE_URL}/api/appointments/`;
      const method = editingAppointmentId ? 'PUT' : 'POST';
      
      // Fix timezone issues by properly handling date conversion
      const localDate = parseISO(newAppointment.date);
      
      // Create a proper ISO string that the server expects
      const isoString = localDate.toISOString();
      
      console.log(`${method} appointment to URL: ${url}`);
      console.log(`Local appointment date: ${newAppointment.date}`);
      console.log(`ISO string date: ${isoString}`);
      
      const appointmentData = {
        ...newAppointment,
        date: isoString, // Send ISO string to server
        customer: id
      };
      
      console.log(`Appointment data:`, appointmentData);

      const response = await authFetch(url, {
        method: method,
        body: JSON.stringify(appointmentData),
      });
      
      console.log(`${method} response status: ${response.status}`);
      
      if (!response.ok) {
        // Try to parse the error response
        try {
          const errorData = await response.json();
          console.error(`${method} error details:`, errorData);
          showStatus(`Failed to save appointment: ${JSON.stringify(errorData)}`, 'error');
        } catch (parseError) {
          console.error('Could not parse error response:', parseError);
          showStatus(`Failed to save appointment. Status: ${response.status}`, 'error');
        }
        return;
      }

      setNewAppointment({ title: '', date: '', location: '', note: '' });
      setEditingAppointmentId(null);
      setShowAppointmentForm(false);
      await fetchData();
      showStatus(`Appointment ${editingAppointmentId ? 'updated' : 'created'} successfully!`, 'success');
    } catch (error) {
      console.error('Error with appointment:', error);
      showStatus('An error occurred while saving the appointment.', 'error');
    }
  };

  const handleDeleteAppointment = async (apptId) => {
    // Confirm deletion
    if (!window.confirm("Are you sure you want to delete this appointment? This action cannot be undone.")) {
      return;
    }
    
    try {
      // FIXED: Wait for CSRF token and verify it was received
      const csrfToken = await fetchCsrfToken();
      if (!csrfToken) {
        throw new Error('Could not get CSRF token');
      }
      console.log('Appointment delete - CSRF token received:', csrfToken.substring(0, 10) + '...');
      
      console.log(`Attempting to delete appointment with ID: ${apptId}`);
      
      // UPDATED: Make sure the URL is correct
      const deleteUrl = `${BASE_URL}/api/appointments/${apptId}/`;
      console.log(`Delete URL: ${deleteUrl}`);
      
      const response = await authFetch(deleteUrl, { 
        method: 'DELETE' 
      });
      
      console.log(`Delete response status: ${response.status}`);
      
      if (!response.ok) {
        // Try to parse the error response
        try {
          const errorData = await response.json();
          console.error('Delete error details:', errorData);
          showStatus(`Failed to delete appointment: ${JSON.stringify(errorData)}`, 'error');
        } catch (parseError) {
          console.error('Could not parse error response:', parseError);
          showStatus(`Failed to delete appointment. Status: ${response.status}`, 'error');
        }
        return;
      }
      
      console.log('Delete successful, refreshing data...');
      await fetchData();
      showStatus('Appointment deleted successfully!', 'success');
    } catch (error) {
      console.error('Error deleting appointment:', error);
      showStatus('An error occurred while deleting the appointment.', 'error');
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) {
      showStatus('Note content cannot be empty.', 'error');
      return;
    }
    
    try {
      // FIXED: Wait for CSRF token and verify it was received
      const csrfToken = await fetchCsrfToken();
      if (!csrfToken) {
        throw new Error('Could not get CSRF token');
      }
      console.log('Note add - CSRF token received:', csrfToken.substring(0, 10) + '...');
      
      // UPDATED: Added /api/ prefix
      const response = await authFetch(`${BASE_URL}/api/notes/`, {
        method: 'POST',
        body: JSON.stringify({ customer: id, content: newNote }),
      });
      
      if (!response.ok) {
        console.error('Failed to add note:', response.status);
        const errorData = await response.json();
        console.error('Error details:', errorData);
        showStatus('Failed to add note. Please try again.', 'error');
        return;
      }
      
      setNewNote('');
      setShowNoteForm(false);
      await fetchData();
      showStatus('Note added successfully!', 'success');
    } catch (error) {
      console.error('Error adding note:', error);
      showStatus('An error occurred while adding the note.', 'error');
    }
  };

  if (!customer || !currentUser) return <p>Loading...</p>;

  // Define the order of fields to display (basic contact info first, then SSN, etc.)
  const orderedFields = [
    'first_name',
    'last_name',
    'email',
    'phone',
    'dob',
    'ssn_last4',
    'fl_blue_member_id',
    'smoker_status',
    'referral_source',
    'status',
    'app_id'
  ];

  return (
    <div className="max-w-4xl mx-auto p-4">
      <Link to="/" className="mb-4 inline-block text-blue-600 hover:underline">← Return to Dashboard</Link>

      {/* Status Message Banner */}
      {statusMessage.text && (
        <div className={`p-3 mb-4 rounded ${statusMessage.type === 'error' ? 'bg-red-100 text-red-700 border border-red-400' : 'bg-green-100 text-green-700 border border-green-400'}`}>
          {statusMessage.text}
        </div>
      )}

      <div className="mb-6">
        {editingCustomer ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {canEditAllFields ? (
              // ADMIN VIEW: Show all fields
              Object.keys(editedCustomerData).map((field) => {
                if (field === "id" || field === "manual_referral" || field === "manual_county" || field === "ssn_last4") return null;
                
                const label = field.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
                
                if (field === "referral_source") {
                  return (
                    <div key={field} className="col-span-full">
                      <label className="block font-semibold">{label}</label>
                      <select className="w-full border p-2" value={editedCustomerData[field]} onChange={(e) => updateField(field, e.target.value)}>
                        <option value="">Select...</option>
                        {referralOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                      </select>
                      {editedCustomerData[field] === "Manual" && (
                        <input className="mt-1 w-full border p-2" placeholder="Enter manual referral" value={manualReferralInput} onChange={(e) => setManualReferralInput(e.target.value)} />
                      )}
                    </div>
                  );
                }
                
                if (field === "county") {
                  return (
                    <div key={field} className="col-span-full">
                      <label className="block font-semibold">{label}</label>
                      <select className="w-full border p-2" value={editedCustomerData[field]} onChange={(e) => updateField(field, e.target.value)}>
                        <option value="">Select...</option>
                        {countyOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                      </select>
                      {editedCustomerData[field] === "Manual" && (
                        <input className="mt-1 w-full border p-2" placeholder="Enter manual county" value={manualCountyInput} onChange={(e) => setManualCountyInput(e.target.value)} />
                      )}
                    </div>
                  );
                }
                
                if (field === "smoker_status") {
                  return (
                    <div key={field}>
                      <label className="block font-semibold">{label}</label>
                      <select 
                        className="w-full border p-2" 
                        value={editedCustomerData[field] || 'Never'} 
                        onChange={(e) => updateField(field, e.target.value)}
                      >
                        {smokerStatusOptions.map(option => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    </div>
                  );
                }
                
                if (field === "status") {
                  return (
                    <div key={field}>
                      <label className="block font-semibold">Status</label>
                      <select 
                        className="w-full border p-2" 
                        value={editedCustomerData[field] || 'Active'} 
                        onChange={(e) => handleStatusChange(e.target.value)}
                      >
                        {statusOptions.map(option => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </div>
                  );
                }
                
                if (field === "app_id") {
                  return (
                    <div key={field}>
                      <label className="block font-semibold">APP ID</label>
                      <input 
                        className="w-full border p-2" 
                        placeholder="Application ID" 
                        value={editedCustomerData[field] || ""} 
                        onChange={(e) => updateField(field, e.target.value)} 
                      />
                    </div>
                  );
                }
                
                if (field === "phone") {
                  return (
                    <div key={field}>
                      <label className="block font-semibold">Phone</label>
                      <input 
                        type="tel"
                        className="w-full border p-2" 
                        placeholder="Phone (e.g., 352-450-1155)" 
                        value={editedCustomerData[field] || ""} 
                        onChange={(e) => {
                          // Auto-format phone number with dashes
                          let value = e.target.value.replace(/\D/g, ''); // Remove non-digits
                          if (value.length >= 6) {
                            value = value.slice(0, 3) + '-' + value.slice(3, 6) + '-' + value.slice(6, 10);
                          } else if (value.length >= 3) {
                            value = value.slice(0, 3) + '-' + value.slice(3);
                          }
                          updateField(field, value);
                        }}
                      />
                      <p className="text-xs text-gray-500 mt-1">Format: 352-450-1155</p>
                    </div>
                  );
                }
                
                if (field === "zip_code") {
                  return (
                    <div key={field}>
                      <label className="block font-semibold">Zip Code</label>
                      <input 
                        type="text"
                        className="w-full border p-2" 
                        placeholder="Zip Code (5 digits)" 
                        value={editedCustomerData[field] || ""} 
                        onChange={(e) => {
                          // Only allow digits and limit to 5 characters
                          const value = e.target.value.replace(/\D/g, '').slice(0, 5);
                          updateField(field, value);
                        }}
                        maxLength="5"
                      />
                      <p className="text-xs text-gray-500 mt-1">5 digits only (e.g., 32162)</p>
                    </div>
                  );
                }
                
                return (
                  <div key={field}>
                    <label className="block font-semibold">{label}</label>
                    <input className="w-full border p-2" placeholder={field} value={editedCustomerData[field] || ""} onChange={(e) => updateField(field, e.target.value)} />
                  </div>
                );
              })
            ) : (
              // AGENT VIEW: All fields shown, but import-mapped fields are
              // disabled when the customer has been matched by a FL Blue import.
              <>
                {customer?.is_import_locked && (
                  <div className="col-span-full mb-4 bg-amber-50 border border-amber-300 rounded p-3 flex items-start gap-2">
                    <svg className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    <div>
                      <p className="font-semibold text-amber-800">Some fields are locked</p>
                      <p className="text-sm text-amber-700">Fields imported from Florida Blue cannot be edited. Contact an admin if corrections are needed.</p>
                    </div>
                  </div>
                )}
                {Object.keys(editedCustomerData).map((field) => {
                  if (field === "id" || field === "manual_referral" || field === "manual_county" || field === "ssn_last4") return null;

                  const isLocked = customer?.is_import_locked && IMPORT_LOCKED_FIELDS.has(field);
                  const label = field.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
                  const lockedClass = isLocked ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : '';

                  if (field === "referral_source") {
                    return (
                      <div key={field} className="col-span-full">
                        <label className="block font-semibold">{label}</label>
                        <select className="w-full border p-2" value={editedCustomerData[field]} onChange={(e) => updateField(field, e.target.value)}>
                          <option value="">Select...</option>
                          {referralOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                        </select>
                        {editedCustomerData[field] === "Manual" && (
                          <input className="mt-1 w-full border p-2" placeholder="Enter manual referral" value={manualReferralInput} onChange={(e) => setManualReferralInput(e.target.value)} />
                        )}
                      </div>
                    );
                  }

                  if (field === "county") {
                    return (
                      <div key={field} className="col-span-full">
                        <label className="block font-semibold">
                          {label}
                          {isLocked && <span className="ml-1 text-amber-600 text-xs font-normal">(imported)</span>}
                        </label>
                        <select className={`w-full border p-2 ${lockedClass}`} value={editedCustomerData[field]} onChange={(e) => updateField(field, e.target.value)} disabled={isLocked}>
                          <option value="">Select...</option>
                          {countyOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                        </select>
                        {!isLocked && editedCustomerData[field] === "Manual" && (
                          <input className="mt-1 w-full border p-2" placeholder="Enter manual county" value={manualCountyInput} onChange={(e) => setManualCountyInput(e.target.value)} />
                        )}
                      </div>
                    );
                  }

                  if (field === "smoker_status") {
                    return (
                      <div key={field}>
                        <label className="block font-semibold">{label}</label>
                        <select className="w-full border p-2" value={editedCustomerData[field] || 'Never'} onChange={(e) => updateField(field, e.target.value)}>
                          {smokerStatusOptions.map(option => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                      </div>
                    );
                  }

                  if (field === "status") {
                    return (
                      <div key={field}>
                        <label className="block font-semibold">Status</label>
                        <select className="w-full border p-2" value={editedCustomerData[field] || 'Active'} onChange={(e) => handleStatusChange(e.target.value)}>
                          {statusOptions.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                      </div>
                    );
                  }

                  if (field === "app_id") {
                    return (
                      <div key={field}>
                        <label className="block font-semibold">APP ID</label>
                        <input className="w-full border p-2" placeholder="Application ID" value={editedCustomerData[field] || ""} onChange={(e) => updateField(field, e.target.value)} />
                      </div>
                    );
                  }

                  if (field === "phone") {
                    return (
                      <div key={field}>
                        <label className="block font-semibold">
                          Phone
                          {isLocked && <span className="ml-1 text-amber-600 text-xs font-normal">(imported)</span>}
                        </label>
                        <input
                          type="tel"
                          className={`w-full border p-2 ${lockedClass}`}
                          placeholder="Phone (e.g., 352-450-1155)"
                          value={editedCustomerData[field] || ""}
                          disabled={isLocked}
                          onChange={(e) => {
                            let value = e.target.value.replace(/\D/g, '');
                            if (value.length >= 6) {
                              value = value.slice(0, 3) + '-' + value.slice(3, 6) + '-' + value.slice(6, 10);
                            } else if (value.length >= 3) {
                              value = value.slice(0, 3) + '-' + value.slice(3);
                            }
                            updateField(field, value);
                          }}
                        />
                        {!isLocked && <p className="text-xs text-gray-500 mt-1">Format: 352-450-1155</p>}
                      </div>
                    );
                  }

                  if (field === "zip_code") {
                    return (
                      <div key={field}>
                        <label className="block font-semibold">
                          Zip Code
                          {isLocked && <span className="ml-1 text-amber-600 text-xs font-normal">(imported)</span>}
                        </label>
                        <input
                          type="text"
                          className={`w-full border p-2 ${lockedClass}`}
                          placeholder="Zip Code (5 digits)"
                          value={editedCustomerData[field] || ""}
                          disabled={isLocked}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '').slice(0, 5);
                            updateField(field, value);
                          }}
                          maxLength="5"
                        />
                        {!isLocked && <p className="text-xs text-gray-500 mt-1">5 digits only (e.g., 32162)</p>}
                      </div>
                    );
                  }

                  return (
                    <div key={field}>
                      <label className="block font-semibold">
                        {label}
                        {isLocked && <span className="ml-1 text-amber-600 text-xs font-normal">(imported)</span>}
                      </label>
                      <input
                        className={`w-full border p-2 ${lockedClass}`}
                        placeholder={field}
                        value={editedCustomerData[field] || ""}
                        disabled={isLocked}
                        onChange={(e) => updateField(field, e.target.value)}
                      />
                    </div>
                  );
                })}
              </>
            )}
            <div className="col-span-full mt-4">
              <button onClick={handleCustomerEdit} className="mr-2 bg-green-500 text-white px-4 py-1 rounded">Save</button>
              <button onClick={() => setEditingCustomer(false)} className="bg-gray-300 px-4 py-1 rounded">Cancel</button>
            </div>
          </div>
        ) : (
          <>
            <h2 className="text-xl font-bold mb-4">{customer.first_name} {customer.last_name}</h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Use the ordered fields array to control display order */}
              {orderedFields.map(key => {
                if (!customer.hasOwnProperty(key) && key !== 'ssn_last4') return null;
                
                // Handle display labels
                let label;
                if (key === "dob") label = "Date of Birth";
                else if (key === "ssn_last4") label = "SSN";  
                else if (key === "smoker_status") label = "Smoker Status";
                else if (key === "status") label = "Status";
                else if (key === "app_id") label = "APP ID";
                else label = key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
                
                // Handle special display formatting
                let displayValue;
                if (key === "ssn_last4") {
                  displayValue = customer.ssn_last4 ? `xxxx-xxxx-${customer.ssn_last4}` : "N/A";
                } else if (key === "status") {
                  // Show the full label for status
                  const statusOption = statusOptions.find(option => option.value === customer[key]);
                  displayValue = statusOption ? statusOption.label : (customer[key] || "N/A");
                } else {
                  displayValue = customer[key] || "N/A";
                }
                
                return (
                  <div key={key} className="border rounded p-3 bg-gray-50">
                    <p className="text-sm font-semibold text-gray-600">{label}</p>
                    <p>{displayValue}</p>
                  </div>
                );
              })}
              
              <div className="border rounded p-3 bg-gray-50 col-span-full">
                <p className="text-sm font-semibold text-gray-600 mb-2">Address</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div><strong>Street Address:</strong> {customer.street_address || "N/A"}</div>
                  <div><strong>Address Line 2:</strong> {customer.address_line2 || "N/A"}</div>
                  <div><strong>City:</strong> {customer.city || "N/A"}</div>
                  <div><strong>State:</strong> {customer.state || "N/A"}</div>
                  <div><strong>Zip Code:</strong> {customer.zip_code || "N/A"}</div>
                  <div><strong>County:</strong> {customer.county || "N/A"}</div>
                </div>
              </div>
            </div>
            
            {/* Edit button with role-appropriate text */}
            {canEditCustomer && (
              <button 
                onClick={() => setEditingCustomer(true)} 
                className="mt-4 bg-blue-500 text-white px-3 py-1 rounded"
              >
                {canEditAllFields ? 'Edit Customer Info' : 'Edit Status & APP ID'}
              </button>
            )}
          </>
        )}
      </div>

      <div className="mb-4 flex gap-4 border-b pb-2">
        <button className={`px-4 py-2 rounded-t ${activeTab === 'notes' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`} onClick={() => setActiveTab('notes')}>Notes</button>
        <button className={`px-4 py-2 rounded-t ${activeTab === 'appointments' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`} onClick={() => setActiveTab('appointments')}>Appointments</button>
        <button className={`px-4 py-2 rounded-t ${activeTab === 'documents' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`} onClick={() => setActiveTab('documents')}>Documents</button>
        <button
          className={`px-4 py-2 rounded-t ${activeTab === 'forms' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
          onClick={() => setActiveTab('forms')}
        >
          Forms
        </button>
        <button
          className={`px-4 py-2 rounded-t ${activeTab === 'policy' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
          onClick={() => setActiveTab('policy')}
        >
          Policy
        </button>
      </div>

      {activeTab === 'notes' && (
        <div>
          
          <button onClick={() => { setShowNoteForm(!showNoteForm); setNewNote(''); }} className="mb-2 bg-blue-500 text-white px-4 py-1 rounded">
            {showNoteForm ? 'Cancel' : 'Add Note'}
          </button>
          {showNoteForm && (
            <div className="mb-4">
              <textarea ref={noteInputRef} className="w-full border p-2" placeholder="Enter note" value={newNote} onChange={(e) => setNewNote(e.target.value)} />
              <button onClick={handleAddNote} className="mt-2 bg-green-500 text-white px-4 py-1 rounded">
                Save Note
              </button>
            </div>
          )}

          {paginatedNotes.map(note => {
            const localTime = toZonedTime(new Date(note.created_at), timeZone);
            const formattedDate = format(localTime, 'MM-dd-yyyy hh:mm a');
            return (
              <div key={note.id} className="border p-2 mb-2 rounded bg-gray-50 flex justify-between items-start">
                <div>
                  <p>{note.content}</p>
                  <p className="text-sm text-gray-500 mt-1">
                    By: {note.created_by_username || 'Unknown'} on {formattedDate}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {activeTab === 'appointments' && (
        <div>
          <button onClick={() => { setShowAppointmentForm(!showAppointmentForm); setEditingAppointmentId(null); setNewAppointment({ title: '', date: '', location: '', note: '' }); }} className="mb-2 bg-blue-500 text-white px-4 py-1 rounded">
            {showAppointmentForm ? 'Cancel' : 'Add Appointment'}
          </button>
          {showAppointmentForm && (
            <div className="mb-4">
              <input ref={apptTitleRef} className="w-full border p-2 mb-2" placeholder="Title" value={newAppointment.title} onChange={(e) => setNewAppointment({ ...newAppointment, title: e.target.value })} />
              <input className="w-full border p-2 mb-2" placeholder="Date" type="datetime-local" value={newAppointment.date} onChange={(e) => setNewAppointment({ ...newAppointment, date: e.target.value })} />
              <input className="w-full border p-2 mb-2" placeholder="Location" value={newAppointment.location} onChange={(e) => setNewAppointment({ ...newAppointment, location: e.target.value })} />
              <textarea className="w-full border p-2 mb-2" placeholder="Note" value={newAppointment.note} onChange={(e) => setNewAppointment({ ...newAppointment, note: e.target.value })} />
              <button onClick={handleAddOrUpdateAppointment} className="bg-green-500 text-white px-4 py-1 rounded">
                {editingAppointmentId ? 'Update Appointment' : 'Save Appointment'}
              </button>
            </div>
          )}
          {paginatedAppointments.map(appt => {
            // Check if the current user is the creator of this appointment
            const isOwner = appt.created_by === currentUser.id;
            const localTime = toZonedTime(new Date(appt.date), timeZone);
            const formattedDate = format(localTime, 'MM-dd-yyyy hh:mm a');
            
            // Add debug information
            console.log(`Appointment ${appt.id} - Created by: ${appt.created_by}, Current user: ${currentUser.id}, isOwner: ${isOwner}, canEditCustomer: ${canEditCustomer}`);
            console.log(`Appointment ${appt.id} - Original date: ${appt.date}, Local time: ${formattedDate}`);
            
            return (
              <div key={appt.id} className="border p-2 mb-2 rounded bg-gray-50">
                <p><strong>{appt.title}</strong></p>
                <p>{formattedDate}</p>
                <p>{appt.location}</p>
                <p>{appt.note}</p>
                <p className="text-sm text-gray-500 mt-1">
                    By: {appt.created_by_username || 'Unknown'}
                </p>
                {/* Make sure this edit/delete section displays for admins, agents, AND owners */}
                {(canEditCustomer || isOwner) && (
                  <div className="mt-2 flex gap-2">
                    <button 
                      onClick={() => handleEditAppointment(appt)} 
                      className="bg-yellow-400 text-white text-sm px-2 py-0.5 rounded"
                    >
                      Edit
                    </button>
                    <button 
                      onClick={() => handleDeleteAppointment(appt.id)} 
                      className="bg-red-500 text-white text-sm px-2 py-0.5 rounded"
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      
      {activeTab === 'documents' && (
  <div>
    {/* Scan Button - Opens desktop scanner app */}
    <div className="mb-4">
      <ScanButton 
        customerId={customer.id}
        customerName={`${customer.first_name} ${customer.last_name}`}
      />
    </div>
    
    {/* Document List */}
    <DocumentList customerId={id} />
  </div>
)}

      {activeTab === 'forms' && (
        <FormsList customer={customer} />
      )}

      {activeTab === 'policy' && (
        <PolicyTab customerId={id} />
      )}

      {/* Expired Status Modal */}
      {showExpiredModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Status Change: Expired</h3>
            <p className="mb-4 text-gray-600">Please provide an explanation for marking this customer as expired:</p>
            <textarea
              className="w-full border p-3 rounded mb-4 h-24"
              placeholder="Enter explanation for expired status..."
              value={expiredExplanation}
              onChange={(e) => setExpiredExplanation(e.target.value)}
            />
            <div className="flex gap-3">
              <button
                onClick={handleExpiredSubmit}
                className="bg-orange-500 text-white px-4 py-2 rounded hover:bg-orange-600"
              >
                Confirm Expired
              </button>
              <button
                onClick={handleModalCancel}
                className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sold Status APP ID Modal */}
      {showAppIdPrompt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Status Change: Sold</h3>
            <p className="mb-4 text-gray-600">Please enter the APP ID to link this customer with the imported app tracker data:</p>
            <input
              type="text"
              className="w-full border p-3 rounded mb-4"
              placeholder="Enter APP ID..."
              value={appIdInput}
              onChange={(e) => setAppIdInput(e.target.value)}
            />
            <div className="flex gap-3">
              <button
                onClick={handleSoldSubmit}
                className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
              >
                Confirm Sold
              </button>
              <button
                onClick={handleModalCancel}
                className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CustomerProfile;